import Cookies from "js-cookie";
import type { ReactNode } from "react";
import {
	createContext,
	createElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useSession } from "@/hooks/auth-hooks";
import { db } from "@/lib/instantdb";
import { normalizeSubscriptionPlan, type SubscriptionTier } from "@/lib/plans";

export interface Subscription {
	plan: string;
	status: string;
	periodEnd?: Date;
	cancelAtPeriodEnd?: boolean;
	stripeSubscriptionId?: string;
}

interface SubscriptionContextValue {
	subscription: Subscription | null;
	isLoading: boolean;
	isActive: boolean;
	tier: SubscriptionTier;
	isPaid: boolean;
	refetch: () => Promise<void>;
}

const PLAN_COOKIE_NAME = "lw_plan";

function getPlanFromCookie(): SubscriptionTier {
	if (typeof document === "undefined") return "free";
	const value = Cookies.get(PLAN_COOKIE_NAME);
	if (value === "pro" || value === "max") return value;
	return "free";
}

function setPlanCookie(tier: SubscriptionTier): void {
	if (typeof document === "undefined") return;
	Cookies.set(PLAN_COOKIE_NAME, tier, {
		expires: 30,
		sameSite: "lax",
		path: "/",
	});
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(
	null,
);

type SubscriptionResponsePayload = {
	subscription: {
		plan: string;
		status: string;
		periodEnd?: string | null;
		cancelAtPeriodEnd?: boolean;
		stripeSubscriptionId?: string | null;
	} | null;
};

async function fetchActiveSubscription(): Promise<Subscription | null> {
	const res = await fetch("/api/subscription", {
		headers: { Accept: "application/json" },
		credentials: "same-origin",
	});
	if (!res.ok) return null;
	const payload = (await res.json()) as SubscriptionResponsePayload;
	const sub = payload.subscription;
	if (!sub) return null;
	return {
		plan: sub.plan,
		status: sub.status,
		periodEnd: sub.periodEnd ? new Date(sub.periodEnd) : undefined,
		cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
		stripeSubscriptionId: sub.stripeSubscriptionId ?? undefined,
	};
}

export function SubscriptionProvider({ children }: { children: ReactNode }) {
	const { session, user, isPending: isSessionPending } = useSession();
	const userId = user?.id ?? session?.userId ?? null;

	const [subscription, setSubscription] = useState<Subscription | null>(() => {
		const tier = getPlanFromCookie();
		return tier !== "free" ? { plan: tier, status: "active" } : null;
	});
	const [isLoading, setIsLoading] = useState(true);
	const [refreshToken, setRefreshToken] = useState(0);

	const requestIdRef = useRef(0);
	const lastFetchedKeyRef = useRef<string | null>(null);

	const { data: activeWorkspaceData } = db.useQuery(
		userId
			? {
					workspaceMembers: {
						$: { where: { "user.id": userId, isActive: true } },
						workspace: {},
					},
				}
			: null,
	);
	const activeWorkspaceId =
		(
			activeWorkspaceData?.workspaceMembers?.[0]?.workspace as
				| { id?: string }
				| null
				| undefined
		)?.id ?? null;

	const refetch = useCallback(async () => {
		if (!userId) return;
		setRefreshToken((v) => v + 1);
	}, [userId]);

	useEffect(() => {
		if (isSessionPending) {
			// Wait for the session to resolve before deciding whether to fetch or
			// clear subscription state; otherwise consumers briefly see
			// isLoading=false + subscription=null and may incorrectly redirect to
			// /choose-plan even though the user has an active subscription.
			return;
		}

		if (!userId) {
			setSubscription(null);
			setPlanCookie("free");
			requestIdRef.current += 1;
			lastFetchedKeyRef.current = null;
			setIsLoading(false);
			return;
		}

		const fetchKey = `${userId}:${activeWorkspaceId ?? "none"}:${refreshToken}`;
		if (lastFetchedKeyRef.current === fetchKey) return;

		requestIdRef.current += 1;
		const requestId = requestIdRef.current;
		setIsLoading(true);

		(async () => {
			try {
				const sub = await fetchActiveSubscription();
				if (requestId !== requestIdRef.current) return;

				setSubscription(sub);
				setPlanCookie(normalizeSubscriptionPlan(sub?.plan).tier);
				lastFetchedKeyRef.current = fetchKey;
			} catch {
				if (requestId !== requestIdRef.current) return;
				setSubscription(null);
				setPlanCookie("free");
				lastFetchedKeyRef.current = fetchKey;
			} finally {
				if (requestId === requestIdRef.current) {
					setIsLoading(false);
				}
			}
		})();
	}, [isSessionPending, userId, activeWorkspaceId, refreshToken]);

	const isActive = subscription?.status === "active";
	const tier = normalizeSubscriptionPlan(subscription?.plan).tier;
	const isPaid = isActive && tier !== "free";

	const value = useMemo<SubscriptionContextValue>(
		() => ({
			subscription,
			isLoading,
			isActive,
			tier,
			isPaid,
			refetch,
		}),
		[subscription, isLoading, isActive, tier, isPaid, refetch],
	);

	return createElement(SubscriptionContext.Provider, { value }, children);
}

export function useSubscription(_userId?: string): SubscriptionContextValue {
	const context = useContext(SubscriptionContext);

	if (!context) {
		const tier = getPlanFromCookie();
		const subscription =
			tier !== "free" ? { plan: tier, status: "active" } : null;
		const isActive = subscription?.status === "active";

		return {
			subscription,
			isLoading: false,
			isActive: !!isActive,
			tier,
			isPaid: !!isActive && tier !== "free",
			refetch: async () => {},
		};
	}

	return context;
}
